// De tekenlaag van Verkennen "eenvoudig" (editie 11): één SVG-kaart op vaste
// plekken met een camera. Ver = gebieden, dichterbij = de onderwerpen van één
// gebied, dichtstbij = de skills van één onderwerp. De lagen vloeien per bol
// over (K2). Scroll of knijp zoomt rond de cursor, slepen schuift, klik = één
// stand dieper, dubbelklik = ×2.
//
// Bewust zonder React: de camera beweegt 60 keer per seconde en een paar
// honderd cirkels opnieuw door React halen kost meer dan het oplevert. React
// houdt de toestand (knop, gekozen skill, Bouwen, afspelen) en vraagt hier om
// opnieuw tekenen; de motor meldt terug op welke afstand je bent.
//
// Port van docs/input/frontend/verkennen-editie-5/editie11.app.js.

import {
  type Nadruk, type Positie, type Venn, type Wereld, clamp, nadruk, owVan, studentTop, type Preset,
} from "../lib/eenvoudig";

const NS = "http://www.w3.org/2000/svg";
type Attr = Record<string, string | number>;
function sv<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Attr | null, parent: Element | null): SVGElementTagNameMap[K] {
  const e = document.createElementNS(NS, tag);
  if (attrs) for (const k in attrs) e.setAttribute(k, String(attrs[k]));
  if (parent) parent.appendChild(e);
  return e;
}
function leeg(e: Element) { while (e.firstChild) e.removeChild(e.firstChild); }

export type MotorStaat = {
  start: boolean; p: Preset | null; sel: number | null; bouw: number[] | null;
  speel: boolean; mi: number; alleenBevestigd: boolean; venn: Venn | null;
};
export type Cam = { x: number; y: number; k: number };
type Lab = SVGTextElement & { _regels?: string[] };
type Bol = { x: number; y: number; r: number; _p?: [number, number]; _r?: number; _v?: number; _z?: number };

export class EenvoudigMotor {
  W = 800; H = 540; cam: Cam = { x: 0, y: 0, k: 1 };
  f: Positie = { s: 1, t: 0, o: null };
  private anim = 0; private raf = 0; private sleutel = "";
  private L: Record<string, SVGGElement> = {};
  private th: { c: SVGCircleElement; lab: Lab; sub: Lab | null; gl: SVGTextElement }[] = [];
  private ow: { c: SVGCircleElement; lab: Lab; gl: SVGTextElement }[] = [];
  private sk: SVGCircleElement[] = [];
  private bol = { th: [] as Bol[], ow: [] as Bol[], sk: [] as Bol[] };
  private X0 = Infinity; private X1 = -Infinity; private Y0 = Infinity; private Y1 = -Infinity;
  private ptrs = new Map<number, [number, number]>();
  private drag: { cam: Cam; p0: [number, number][]; moved: boolean } | null = null;
  private bewogen = false;
  private ro: ResizeObserver | null = null;
  private weg: (() => void)[] = [];
  private rm: boolean;

  constructor(
    private svg: SVGSVGElement,
    private host: HTMLElement,
    private w: Wereld,
    private haal: () => MotorStaat,
    private melding: { onF: (f: Positie) => void; onSkill: (i: number) => void; onTip: (t: string | null, x: number, y: number) => void; onGezoomd: () => void; onBeweeg: () => void },
  ) {
    this.rm = typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    for (const n of ["thema", "ow", "sk", "lijn", "ring", "label", "badge", "venn"]) this.L[n] = sv("g", { class: "l-" + n }, svg);
    w.gebieden.forEach((t) => {
      const c = sv("circle", { class: "b", "data-k": "g", "data-i": t.i }, this.L.thema!); c.style.fill = t.kleur; c.style.stroke = t.kleur;
      const gl = sv("text", { "text-anchor": "middle", "dominant-baseline": "middle" }, this.L.label!); gl.style.display = "none";
      this.th.push({ c, lab: this.maakLabel(t.naam, "k-lab"), sub: t.dun ? this.maakLabel("weinig rapporten", "k-sub") : null, gl });
      this.bol.th.push(t);
      this.X0 = Math.min(this.X0, t.x - t.r); this.X1 = Math.max(this.X1, t.x + t.r); this.Y0 = Math.min(this.Y0, t.y - t.r); this.Y1 = Math.max(this.Y1, t.y + t.r);
    });
    w.onderwerpen.forEach((o) => {
      const c = sv("circle", { class: "b" + (o.opkomend ? " opkomend" : "") + (o.niveau === "deel" ? " deel" : ""), "data-k": "o", "data-i": o.i }, this.L.ow!);
      c.style.fill = o.kleur; c.style.stroke = o.kleur;
      const gl = sv("text", { "text-anchor": "middle", "dominant-baseline": "middle" }, this.L.label!); gl.style.display = "none";
      this.ow.push({ c, lab: this.maakLabel(o.naam, "k-lab"), gl });
      this.bol.ow.push(o);
    });
    w.skills.forEach((s) => {
      const c = sv("circle", { class: "b b-sk", "data-k": "s", "data-i": s.i }, this.L.sk!); c.style.fill = w.onderwerpen[s.ow]!.kleur;
      this.sk.push(c); this.bol.sk.push(s);
    });
    if (!w.gebieden.length) { this.X0 = -100; this.X1 = 100; this.Y0 = -100; this.Y1 = 100; }
    this.koppel();
    this.resize(true);
    this.cam = this.fitAll();
  }

  destroy() { cancelAnimationFrame(this.anim); cancelAnimationFrame(this.raf); this.ro?.disconnect(); this.weg.forEach((f) => f()); leeg(this.svg); }

  // ── camera ───────────────────────────────────────────────────────────────
  private mwh() { return Math.min(this.W, this.H); }
  fitAll(): Cam { const k = Math.min((this.W - 60) / (this.X1 - this.X0), (this.H - 80) / (this.Y1 - this.Y0)); return { x: (this.X0 + this.X1) / 2, y: (this.Y0 + this.Y1) / 2 + 14 / k, k }; }
  fitC(c: { x: number; y: number; r: number }, f: number): Cam { return { x: c.x, y: c.y, k: (f * this.mwh()) / Math.max(c.r, 1e-6) }; }
  private kgr(k: number) {
    const lo = this.fitAll().k * 0.8; let hi = lo * 4;
    for (const o of this.w.onderwerpen) hi = Math.max(hi, (0.9 * this.mwh()) / Math.max(o.r, 1e-6));
    return clamp(k, lo, hi);
  }
  private sp(x: number, y: number): [number, number] { return [this.W / 2 + (x - this.cam.x) * this.cam.k, this.H / 2 + (y - this.cam.y) * this.cam.k]; }
  tween(to: Cam, ms = 480) {
    const doel = { x: to.x, y: to.y, k: this.kgr(to.k) };
    cancelAnimationFrame(this.anim);
    if (this.rm || typeof requestAnimationFrame === "undefined") { this.cam = doel; this.render(); return; }
    const van = { ...this.cam }, t0 = performance.now();
    const stap = (nu: number) => {
      const u = clamp((nu - t0) / ms, 0, 1), e = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
      this.cam = { x: van.x + (doel.x - van.x) * e, y: van.y + (doel.y - van.y) * e, k: Math.exp(Math.log(van.k) + (Math.log(doel.k) - Math.log(van.k)) * e) };
      this.render();
      if (u < 1) this.anim = requestAnimationFrame(stap);
    };
    this.anim = requestAnimationFrame(stap);
  }
  plan() { if (this.raf) return; this.raf = requestAnimationFrame(() => { this.raf = 0; this.render(); }); }
  naarGebied(i: number) { const t = this.w.gebieden[i]; if (t) { this.melding.onGezoomd(); this.tween(this.fitC(t, 0.43)); } }
  naarOnderwerp(i: number) { const o = this.w.onderwerpen[i]; if (o) { this.melding.onGezoomd(); this.tween(this.fitC(o, 0.42)); } }
  naarVeld() { this.tween(this.fitAll()); }
  /** De gekozen skill in beeld; staat het kaartje rechts over de kaart, dan schuift de kaart iets naar links. */
  toonSkill(i: number, rechts: boolean) {
    const s = this.w.skills[i]; if (!s) return;
    this.tween({ x: s.x + (rechts ? (this.W / 2 - 0.27 * this.W) / this.cam.k : 0), y: s.y, k: this.cam.k }, 350);
  }
  /** Waar een bol nu op het scherm staat (voor de Playwright-proef en het kaartje). */
  scherm(soort: "g" | "o" | "s", i: number): [number, number] | null {
    const b = soort === "g" ? this.bol.th[i] : soort === "o" ? this.bol.ow[i] : this.bol.sk[i];
    return b ? this.sp(b.x, b.y) : null;
  }
  zoomIn() {
    this.melding.onGezoomd();
    if (this.f.s === 1) { const t = this.dichtst(this.bol.th); if (t) this.tween(this.fitC(t, 0.43)); }
    else if (this.f.s === 2) { const o = this.dichtst(owVan(this.w, this.w.gebieden[this.f.t]!)); if (o) this.tween(this.fitC(o, 0.42)); }
    else this.tween({ ...this.cam, k: this.cam.k * 1.6 });
  }
  zoomUit() {
    if (this.f.s === 3) this.naarGebied(this.f.t); else if (this.f.s === 2) this.naarVeld(); else this.tween({ ...this.cam, k: this.cam.k / 1.4 });
  }
  schuif(dx: number, dy: number) { const st = 60 / this.cam.k; this.cam = { ...this.cam, x: this.cam.x + dx * st, y: this.cam.y + dy * st }; this.plan(); }

  private dichtst<T extends Bol>(lijst: T[]): T | null {
    let b: T | null = null, bd = Infinity;
    for (const c of lijst) { const d = Math.hypot(c.x - this.cam.x, c.y - this.cam.y) - c.r; if (d < bd) { bd = d; b = c; } }
    return b;
  }
  /** Stand 1 = gebieden, 2 = onderwerpen van één gebied, 3 = skills van één onderwerp. */
  bepaal(): Positie {
    const m = this.mwh(), sel = this.haal().sel;
    if (!this.w.gebieden.length) return { s: 1, t: 0, o: null };
    if (sel !== null) {
      const so = this.w.onderwerpen[this.w.skills[sel]!.ow]!;
      if (this.cam.k * so.r >= 0.3 * m) return { s: 3, t: so.t, o: so.i };
    }
    const t = this.dichtst(this.w.gebieden)!;
    if (this.cam.k * t.r >= 0.3 * m) {
      const o = this.dichtst(owVan(this.w, t));
      if (o && o.sk.length && this.cam.k * o.r >= 0.3 * m) return { s: 3, t: t.i, o: o.i };
      return { s: 2, t: t.i, o: o ? o.i : null };
    }
    return { s: 1, t: t.i, o: null };
  }
  private vast(rs: number, inhoud: boolean) { if (!inhoud) return 1; const m = this.mwh(); return clamp((0.34 * m - rs) / (0.12 * m), 0, 1); }

  // ── tekenen ──────────────────────────────────────────────────────────────
  private regels(naam: string): string[] {
    if (naam.length <= 14 || naam.indexOf(" ") < 0) return [naam];
    const mid = naam.length / 2; let best = -1;
    for (let i = 0; i < naam.length; i++) if (naam[i] === " " && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
    return [naam.slice(0, best), naam.slice(best + 1)];
  }
  private maakLabel(naam: string, cls: string): Lab {
    const t = sv("text", { class: cls, "text-anchor": "middle", "dominant-baseline": "middle" }, this.L.label!) as Lab, r = this.regels(naam);
    r.forEach((s, i) => { const ts = sv("tspan", { dy: i === 0 ? -(r.length - 1) * 0.6 + "em" : "1.2em" }, t); ts.textContent = s; });
    t._regels = r; t.style.display = "none";
    return t;
  }
  private zetLabel(t: Lab, x: number, y: number, op: number) {
    if (op <= 0.03) { t.style.display = "none"; return; }
    t.style.display = ""; t.setAttribute("y", y.toFixed(1));
    for (let i = 0; i < t.childNodes.length; i++) (t.childNodes[i] as Element).setAttribute("x", x.toFixed(1));
    t.style.opacity = op.toFixed(2);
  }
  private past(t: Lab, r: number) { const l = Math.max(...(t._regels ?? [""]).map((s) => s.length)); return r >= 38 || 2 * r >= 7.2 * l + 10; }
  private zetC(c: SVGCircleElement, p: [number, number], r: number) { c.setAttribute("cx", p[0].toFixed(1)); c.setAttribute("cy", p[1].toFixed(1)); c.setAttribute("r", Math.max(r, 0.5).toFixed(1)); }
  private zichtbaar(p: [number, number], r: number) { const nx = clamp(p[0], 0, this.W), ny = clamp(p[1], 0, this.H); return Math.hypot(nx - p[0], ny - p[1]) < r; }
  private groei(o: { cum: number[] }) {
    const st = this.haal(); if (!st.speel || !o.cum.length) return 1;
    return Math.sqrt(Math.max(o.cum[Math.min(st.mi, o.cum.length - 1)]!, 0) / Math.max(o.cum[o.cum.length - 1]!, 1));
  }
  private gebiedLabel(t: SVGTextElement, naam: string, kleur: string, p: [number, number], r: number, op: number) {
    const cx = this.W / 2, cy = this.H / 2, d = Math.hypot(p[0] - cx, p[1] - cy); let x: number, y: number;
    if (d < r) { x = p[0]; y = Math.max(p[1] - r + 20, 18); t.setAttribute("class", "k-gebied"); t.style.fill = kleur; t.textContent = naam.toUpperCase(); }
    else {
      const ins = Math.min(22, r * 0.5);
      x = p[0] + ((cx - p[0]) / d) * (r - ins); y = p[1] + ((cy - p[1]) / d) * (r - ins);
      t.setAttribute("class", "k-buur"); t.style.fill = ""; t.textContent = naam;
    }
    x = clamp(x, 70, this.W - 70); y = clamp(y, 16, this.H - 14);
    if (Math.hypot(x - p[0], y - p[1]) > r - 4 || op < 0.05) { t.style.display = "none"; return; }
    t.style.display = ""; t.setAttribute("x", x.toFixed(1)); t.setAttribute("y", y.toFixed(1)); t.style.opacity = op.toFixed(2);
  }
  private badge(x: number, y: number, n: number) {
    const g = sv("g", { class: "badge" }, this.L.badge!);
    sv("circle", { cx: x.toFixed(1), cy: y.toFixed(1), r: 10 }, g);
    const t = sv("text", { x: x.toFixed(1), y: (y + 0.5).toFixed(1), "text-anchor": "middle", "dominant-baseline": "middle" }, g); t.textContent = String(n);
  }
  private bolVan(ref: ["g" | "o", number]): Bol & { naam: string } { return (ref[0] === "g" ? this.w.gebieden[ref[1]] : this.w.onderwerpen[ref[1]]) as Bol & { naam: string }; }
  private lijn(l: Nadruk["lijnen"][number]) {
    const A = this.bolVan(l.a), B = this.bolVan(l.b), a = A._p, b = B._p; if (!a || !b) return;
    const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2, dx = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dx, dy) || 1;
    const cx = mx - (dy / len) * len * 0.18, cy = my + (dx / len) * len * 0.18;
    const pth = sv("path", { d: `M${a[0].toFixed(1)} ${a[1].toFixed(1)} Q${cx.toFixed(1)} ${cy.toFixed(1)} ${b[0].toFixed(1)} ${b[1].toFixed(1)}`, class: "lijn" + (l.stip ? " stip" : "") }, this.L.lijn!);
    pth.style.strokeWidth = String(l.w);
    const t = sv("text", { x: ((mx + cx) / 2).toFixed(1), y: ((my + cy) / 2).toFixed(1), class: "k-lijn", "text-anchor": "middle", "dominant-baseline": "middle" }, this.L.lijn!); t.textContent = l.tekst;
    if (l.punten) for (const o of [A, B]) {
      sv("circle", { cx: o._p![0].toFixed(1), cy: o._p![1].toFixed(1), r: 5, class: "stippunt" }, this.L.lijn!);
      const n = sv("text", { x: o._p![0].toFixed(1), y: (o._p![1] - 12).toFixed(1), class: "k-lab klein", "text-anchor": "middle" }, this.L.lijn!); n.textContent = o.naam;
    }
  }

  render() {
    const st = this.haal();
    if (st.start || !this.W) return;
    if (st.bouw) { this.tekenVenn(st); return; }
    this.L.venn!.style.display = "none";
    for (const n of ["thema", "ow", "sk", "lijn", "ring", "label", "badge"]) this.L[n]!.style.display = "";
    const w = this.w;
    this.f = this.bepaal();
    const key = this.f.s + ":" + this.f.t + ":" + (this.f.o ?? "-");
    if (key !== this.sleutel) { this.sleutel = key; this.melding.onF({ ...this.f }); }
    if (!w.gebieden.length) return;
    const nd = nadruk(w, st.p, this.f, st.sel, st.alleenBevestigd);
    const fo = this.f.o !== null ? w.onderwerpen[this.f.o]! : null;
    const top = this.f.s === 3 && fo ? studentTop(w, fo, st.alleenBevestigd) : null;
    const skA = (i: number) => {
      const s = w.skills[i]!;
      if (st.sel !== null) return i === st.sel ? 1 : 0.25;
      if (st.alleenBevestigd && !s.nb) return 0.18;
      if (top && st.p === "student") return top.top.includes(s) ? 1 : top.gelijk.includes(s) ? 0.6 : 0.28;
      return 0.85;
    };
    w.gebieden.forEach((t, i) => {
      const e = this.th[i]!, b = this.bol.th[i]!;
      const p = this.sp(t.x, t.y), r = t.r * this.cam.k * this.groei(t), v = this.vast(t.r * this.cam.k, true);
      b._p = p; b._r = r; b._v = v;
      this.zetC(e.c, p, r);
      e.c.style.fillOpacity = ((0.07 + 0.73 * v) * (t.dun ? 0.45 : 1)).toFixed(3);
      e.c.style.strokeOpacity = ((1 - v) * 0.45).toFixed(3);
      if (v > 0.5) {
        const bin = this.past(e.lab, r), nr = e.lab._regels!.length;
        this.zetLabel(e.lab, p[0], bin ? p[1] : p[1] + r + 7.5 + 7.5 * nr, v);
        if (e.sub) this.zetLabel(e.sub, p[0], bin ? p[1] + r + 15 : p[1] + r + 15 * nr + 16, v);
        e.gl.style.display = "none";
      } else {
        e.lab.style.display = "none"; if (e.sub) e.sub.style.display = "none";
        this.gebiedLabel(e.gl, t.naam, t.kleur, p, r, 1 - v);
      }
    });
    w.onderwerpen.forEach((o, i) => {
      const e = this.ow[i]!, b = this.bol.ow[i]!;
      const z = 1 - (this.bol.th[o.t]!._v ?? 1), p = this.sp(o.x, o.y), r = o.r * this.cam.k * this.groei(o), v = this.vast(o.r * this.cam.k, o.sk.length > 0);
      b._p = p; b._r = r; b._v = v; b._z = z;
      if (z < 0.02 || !this.zichtbaar(p, r)) { e.c.style.display = "none"; e.lab.style.display = "none"; e.gl.style.display = "none"; return; }
      e.c.style.display = ""; this.zetC(e.c, p, r);
      e.c.style.fillOpacity = (z * (0.07 + 0.73 * v) * (o.dun ? 0.4 : 1) * (o.opkomend ? 0.6 : 1)).toFixed(3);
      e.c.style.strokeOpacity = (o.opkomend ? z * 0.9 : z * (1 - v) * 0.45).toFixed(3);
      e.c.style.pointerEvents = z > 0.35 ? "auto" : "none";
      if (z * v > 0.5) {
        const bin = this.past(e.lab, r);
        this.zetLabel(e.lab, p[0], bin ? p[1] : p[1] + r + 7.5 + 7.5 * e.lab._regels!.length, z * v); e.gl.style.display = "none";
      } else if (z > 0.5) { e.lab.style.display = "none"; this.gebiedLabel(e.gl, o.naam, o.kleur, p, r, z * (1 - v)); }
      else { e.lab.style.display = "none"; e.gl.style.display = "none"; }
    });
    w.skills.forEach((s, i) => {
      const c = this.sk[i]!, b = this.bol.sk[i]!, ob = this.bol.ow[s.ow]!;
      const z = (ob._z ?? 0) * (1 - (ob._v ?? 1));
      const p = this.sp(s.x, s.y), r = s.r * this.cam.k; b._p = p; b._r = r;
      const inTijd = !st.speel || s.van === null || s.van <= (w.maanden[st.mi] ?? w.nu);
      if (z < 0.02 || !inTijd || !this.zichtbaar(p, r)) { c.style.display = "none"; return; }
      c.style.display = ""; this.zetC(c, p, r);
      c.style.fillOpacity = (z * (fo && s.ow === fo.i ? skA(i) : 0.5)).toFixed(3);
      c.style.pointerEvents = z > 0.35 ? "auto" : "none";
    });
    leeg(this.L.ring!); leeg(this.L.badge!); leeg(this.L.lijn!);
    nd.lijnen.forEach((l) => this.lijn(l));
    // De bewijslaag van 01-10: een groene ring om skills die een bevestigde ontwikkeling draagt.
    if (this.f.s === 3 && fo) for (const si of fo.sk) {
      const s = w.skills[si]!, b = this.bol.sk[si]!, c = this.sk[si]!;
      if (!s.nb || c.style.display === "none" || !b._p) continue;
      sv("circle", { cx: b._p[0].toFixed(1), cy: b._p[1].toFixed(1), r: ((b._r ?? 0) + 2.6).toFixed(1), class: "ring bevestigd" }, this.L.ring!);
    }
    nd.items.forEach((it) => {
      const b = it.soort === "g" ? this.bol.th[it.i] : it.soort === "o" ? this.bol.ow[it.i] : this.bol.sk[it.i];
      const el = it.soort === "g" ? this.th[it.i]?.c : it.soort === "o" ? this.ow[it.i]?.c : this.sk[it.i];
      if (!b?._p || !el || el.style.display === "none") return;
      const r = b._r ?? 0;
      if (it.w) { const rg = sv("circle", { cx: b._p[0].toFixed(1), cy: b._p[1].toFixed(1), r: (r + 3 + it.w / 2).toFixed(1), class: "ring" }, this.L.ring!); rg.style.strokeWidth = String(it.w); }
      if (it.sel) sv("circle", { cx: b._p[0].toFixed(1), cy: b._p[1].toFixed(1), r: (r + 5).toFixed(1), class: "ring sel" }, this.L.ring!);
      if (it.nr) this.badge(b._p[0] - r * 0.72, b._p[1] - r * 0.72, it.nr);
    });
  }

  private tekenVenn(st: MotorStaat) {
    for (const n of ["thema", "ow", "sk", "lijn", "ring", "label", "badge"]) this.L[n]!.style.display = "none";
    const L = this.L.venn!; leeg(L); L.style.display = "";
    const v = st.venn, W = this.W, H = this.H, m = this.mwh();
    if (!v || !v.ids.length) {
      const t0 = sv("text", { x: W / 2, y: H / 2, class: "k-sub groot", "text-anchor": "middle" }, L); t0.textContent = "Kies bovenaan twee of drie onderwerpen";
      return;
    }
    const n = v.ids.length, sets = v.ids.map((i) => this.w.onderwerpen[i]!.skillSet);
    let R = v.maten.map((s) => Math.sqrt(Math.max(s, 1))); const mx = Math.max(...R);
    R = R.map((z) => (0.27 * m * z) / mx);
    const afst = (i: number, j: number) => {
      let g = 0; sets[i]!.forEach((x) => { if (sets[j]!.has(x)) g++; });
      const f = clamp(g / Math.max(1, Math.min(sets[i]!.size, sets[j]!.size)), 0.12, 0.92);
      return R[i]! + R[j]! - (R[i]! + R[j]! - Math.abs(R[i]! - R[j]!)) * f;
    };
    let pos: [number, number][];
    if (n === 1) pos = [[0, 0]];
    else if (n === 2) pos = [[0, 0], [afst(0, 1), 0]];
    else {
      const dab = afst(0, 1), dac = afst(0, 2), dbc = afst(1, 2), x = (dac * dac - dbc * dbc + dab * dab) / (2 * dab);
      const y = Math.sqrt(Math.max(dac * dac - x * x, Math.pow(0.35 * Math.min(dac, dbc), 2)));
      pos = [[0, 0], [dab, 0], [x, y]];
    }
    let bx0 = Infinity, bx1 = -Infinity, by0 = Infinity, by1 = -Infinity;
    pos.forEach((p, i) => { bx0 = Math.min(bx0, p[0] - R[i]!); bx1 = Math.max(bx1, p[0] + R[i]!); by0 = Math.min(by0, p[1] - R[i]!); by1 = Math.max(by1, p[1] + R[i]!); });
    const sc = Math.min((W - 90) / (bx1 - bx0), (H - 110) / (by1 - by0), 1.6), ox = W / 2 - ((bx0 + bx1) / 2) * sc, oy = H / 2 + 4 - ((by0 + by1) / 2) * sc;
    const C = pos.map((p, i) => [ox + p[0] * sc, oy + p[1] * sc, R[i]! * sc] as const);
    C.forEach((c, i) => {
      const k = this.w.onderwerpen[v.ids[i]!]!.kleur, e = sv("circle", { cx: c[0].toFixed(1), cy: c[1].toFixed(1), r: c[2].toFixed(1), class: "venn-c" }, L);
      e.style.fill = k; e.style.stroke = k;
    });
    const acc = new Map<number, [number, number, number]>();
    for (let gx = 0; gx < W; gx += 4) for (let gy = 0; gy < H; gy += 4) {
      let mk = 0; C.forEach((c, j) => { if (Math.hypot(gx - c[0], gy - c[1]) <= c[2]) mk |= 1 << j; });
      if (mk) { const a = acc.get(mk) ?? [0, 0, 0]; a[0] += gx; a[1] += gy; a[2]++; acc.set(mk, a); }
    }
    for (const [mk, aantal] of v.perMask) {
      const a = acc.get(mk); if (!a) continue;
      const x = a[0] / a[2], y = a[1] / a[2], bits = mk.toString(2).replace(/0/g, "").length, midden = n > 1 && bits === n;
      const t = sv("text", { x: x.toFixed(1), y: (midden ? y - 6 : y).toFixed(1), class: midden ? "k-venn-grot" : "k-venn", "text-anchor": "middle", "dominant-baseline": "middle" }, L);
      t.textContent = String(aantal);
      if (midden) { const u = sv("text", { x: x.toFixed(1), y: (y + 16).toFixed(1), class: "k-sub", "text-anchor": "middle", "dominant-baseline": "middle" }, L); u.textContent = n === 3 ? "in alle drie" : "in beide"; }
    }
    C.forEach((c, i) => {
      const o = this.w.onderwerpen[v.ids[i]!]!, onder = n === 3 && i === 2, yy = onder ? c[1] + c[2] + 16 : c[1] - c[2] - 12;
      const xx = n === 1 ? c[0] : i === 0 ? c[0] - c[2] * 0.3 : i === 1 ? c[0] + c[2] * 0.3 : c[0];
      const t = sv("text", { x: clamp(xx, 90, W - 90).toFixed(1), y: clamp(yy, 14, H - 26).toFixed(1), class: "k-lab", "text-anchor": "middle", "dominant-baseline": "middle" }, L);
      t.textContent = o.naam + " · " + v.maten[i];
    });
    const lg = sv("text", { x: 14, y: H - 12, class: "k-sub links" }, L); lg.textContent = "Het getal in elk vlak is het aantal skills.";
  }

  // ── zoomen, schuiven, klikken ────────────────────────────────────────────
  resize(reset = false) {
    const w = this.host.clientWidth, h = this.host.clientHeight;
    if (!w || !h) return;
    const oud = this.mwh(); this.W = w; this.H = h; this.svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    if (!reset && oud) this.cam = { ...this.cam, k: this.cam.k * (this.mwh() / oud) };
    this.render();
  }
  private zoomOm(sx: number, sy: number, f: number, basis?: Cam) {
    const b = basis ?? this.cam, wx = b.x + (sx - this.W / 2) / b.k, wy = b.y + (sy - this.H / 2) / b.k, k = this.kgr(b.k * f);
    this.cam = { x: wx - (sx - this.W / 2) / k, y: wy - (sy - this.H / 2) / k, k };
    cancelAnimationFrame(this.anim); this.plan(); this.melding.onGezoomd();
  }
  private binnen(e: { clientX: number; clientY: number }): [number, number] { const r = this.host.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; }
  private aan<K extends keyof WindowEventMap>(doel: EventTarget, type: K | string, fn: (e: never) => void, opt?: AddEventListenerOptions) {
    doel.addEventListener(type, fn as EventListener, opt); this.weg.push(() => doel.removeEventListener(type, fn as EventListener, opt));
  }
  private koppel() {
    const host = this.host;
    if (typeof ResizeObserver !== "undefined") { this.ro = new ResizeObserver(() => this.resize(false)); this.ro.observe(host); }
    this.aan(host, "wheel", (e: WheelEvent) => {
      const st = this.haal(); if (st.start || st.bouw) return;
      e.preventDefault(); this.melding.onTip(null, 0, 0);
      const [x, y] = this.binnen(e), dy = clamp(e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY, -120, 120);
      this.zoomOm(x, y, Math.exp(-dy * 0.0016)); this.melding.onBeweeg();
    }, { passive: false });
    this.aan(host, "pointerdown", (e: PointerEvent) => {
      const st = this.haal(); if (st.start || st.bouw || (e.pointerType === "mouse" && e.button !== 0)) return;
      if ((e.target as Element).closest?.(".zoomctl,.hint")) return;
      this.ptrs.set(e.pointerId, this.binnen(e));
      this.drag = { cam: { ...this.cam }, p0: [...this.ptrs.values()].map((p) => [p[0], p[1]] as [number, number]), moved: this.ptrs.size >= 2 };
    });
    this.aan(window, "pointermove", (e: PointerEvent) => {
      if (!this.drag || !this.ptrs.has(e.pointerId)) return;
      this.ptrs.set(e.pointerId, this.binnen(e));
      const pts = [...this.ptrs.values()], p0 = this.drag.p0;
      if (pts.length === 1 && p0.length === 1) {
        const dx = pts[0]![0] - p0[0]![0], dy = pts[0]![1] - p0[0]![1];
        if (Math.hypot(dx, dy) > 5) this.drag.moved = true;
        if (this.drag.moved) { this.cam = { x: this.drag.cam.x - dx / this.cam.k, y: this.drag.cam.y - dy / this.cam.k, k: this.cam.k }; cancelAnimationFrame(this.anim); this.plan(); this.melding.onTip(null, 0, 0); }
      } else if (pts.length >= 2 && p0.length >= 2) {
        this.drag.moved = true;
        const d0 = Math.hypot(p0[0]![0] - p0[1]![0], p0[0]![1] - p0[1]![1]) || 1, d1 = Math.hypot(pts[0]![0] - pts[1]![0], pts[0]![1] - pts[1]![1]);
        this.zoomOm((pts[0]![0] + pts[1]![0]) / 2, (pts[0]![1] + pts[1]![1]) / 2, d1 / d0, this.drag.cam);
      }
    });
    const los = (e: PointerEvent) => {
      if (!this.ptrs.has(e.pointerId)) return;
      this.ptrs.delete(e.pointerId);
      if (this.drag?.moved) { this.bewogen = true; setTimeout(() => { this.bewogen = false; }, 60); this.melding.onBeweeg(); }
      this.drag = this.ptrs.size ? { cam: { ...this.cam }, p0: [...this.ptrs.values()].map((p) => [p[0], p[1]] as [number, number]), moved: true } : null;
    };
    this.aan(window, "pointerup", los); this.aan(window, "pointercancel", los);
    this.aan(this.svg, "click", (e: MouseEvent) => {
      this.melding.onTip(null, 0, 0);
      if (this.bewogen) return;
      const c = (e.target as Element).closest?.("[data-k]"); if (!c) return;
      const i = Number(c.getAttribute("data-i")), k = c.getAttribute("data-k");
      this.melding.onGezoomd();
      if (k === "g") { if (this.f.s === 1 || this.f.t !== i) this.naarGebied(i); }
      else if (k === "o") { if (!(this.f.s === 3 && this.f.o === i)) this.naarOnderwerp(i); }
      else if (k === "s") this.melding.onSkill(i);
    });
    this.aan(this.svg, "dblclick", (e: MouseEvent) => {
      if (this.haal().bouw || (e.target as Element).closest?.("[data-k]")) return;
      const p = this.binnen(e);
      this.tween({ x: this.cam.x + (p[0] - this.W / 2) / this.cam.k, y: this.cam.y + (p[1] - this.H / 2) / this.cam.k, k: this.cam.k * 2 });
    });
    this.aan(this.svg, "pointermove", (e: PointerEvent) => {
      const c = (e.target as Element).closest?.("[data-k]") as SVGCircleElement | null;
      // Op een aanraakscherm geen zweeftekst: die blijft anders staan na een tik.
      if (e.pointerType && e.pointerType !== "mouse") { this.melding.onTip(null, 0, 0); return; }
      if (!c || this.drag?.moved || this.haal().bouw || Number(c.style.fillOpacity) < 0.3) { this.melding.onTip(null, 0, 0); return; }
      const i = Number(c.getAttribute("data-i")), k = c.getAttribute("data-k"), w = this.w;
      const eenh = w.metRapporten ? " rapporten" : " ontwikkelingen en skills";
      let t: string;
      if (k === "g") { const g = w.gebieden[i]!; t = g.naam + " · " + g.docs + eenh + " · " + Math.round(g.aandeel * 100) + "% nieuw"; }
      else if (k === "o") { const o = w.onderwerpen[i]!; t = o.naam + (o.opkomend ? " (opkomend)" : "") + (o.niveau === "deel" && o.hoofd ? " · deel van " + o.hoofd : "") + " · " + o.docs + eenh + " · " + Math.round(o.aandeel * 100) + "% nieuw"; }
      else { const s = w.skills[i]!; t = s.naam + " · gevraagd door " + s.n + (s.nb ? " (" + s.nb + " bevestigd)" : ""); }
      const p = this.binnen(e); this.melding.onTip(t, p[0], p[1]);
    });
    this.aan(this.svg, "pointerleave", () => this.melding.onTip(null, 0, 0));
  }
}
